import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';

export const OrganDonorIntroScreen4: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();

  const handleNext = () => {
    navigation.navigate('OrganDonorInput');
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
          <View style={styles.content}>
            <Text style={styles.title}>Frequently Asked Questions</Text>
            
            <View style={styles.faqContainer}>
              <Text style={styles.question}>
                <Text style={styles.bold}>1. What are the benefits of organ donation?</Text>
              </Text>
              <Text style={styles.answer}>
                When you donate your organs, you have the potential of transforming up to 10 lives. You're giving recipients the gift of life or the chance for a better quality of life. Tissue donation – like bone, skin, corneas, heart valves and vessels – could impact the lives of up to 75 more people.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>2. Why do some people not want their organs donated after they die?</Text>
              </Text>
              <Text style={styles.answer}>
                During a study by the National Institutes of Health, those opposed to organ donation cited reasons such as mistrust of the system and worrying that their organs would go to someone not deserving of them (e.g., a "bad" person or someone whose poor lifestyle choices caused their illness). Of course, many people have other reasons for not becoming organ donors as well.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>3. Can I be an organ donor if I have underlying health conditions?</Text>
              </Text>
              <Text style={styles.answer}>
                There are a few serious health conditions that could prevent a person from being a donor, including a cancer that's actively spreading and HIV. But even if you're not sure if you're a suitable donor, you should register if it's something you want to do. The transplant team will assess the viability of your organs before proceeding. You can also consider donating your body to research or to a local medical school.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>4. Is there an age limit for being a donor?</Text>
              </Text>
              <Text style={styles.answer}>
                When it comes to organ donation, age doesn't matter. What matters is the health of the donor and the condition of the organs. The oldest known donor of an internal organ was 92 years old. Donors under the age of 18 need parental consent.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>5. Can deceased organ donors still have an open-casket funeral?</Text>
              </Text>
              <Text style={styles.answer}>
                Yes. The incisions are relatively small. Funeral directors are trained to fully prepare the body for open-casket funerals using special techniques, makeup and clothing.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>6. Can my family make a different decision for me after I die?</Text>
              </Text>
              <Text style={styles.answer}>
                If you haven't signed up to be a donor, your family can still give consent to donate your organs after your death. If you've signed up for the national organ donor registry, your family can't prevent your organs from being donated. If you don't want your organs donated under any circumstances, the Uniform Anatomical Gift Act allows you to submit an official refusal. When deciding whether or not to become an organ donor, it is important to discuss your wishes with your family.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>7. Does it cost money to donate organs?</Text>
              </Text>
              <Text style={styles.answer}>
                Organ donation does not cost the donor or the donor's family anything. All costs related to organ donation and transplant are paid by the recipient of the organ.
              </Text>

              <Text style={styles.question}>
                <Text style={styles.bold}>8. How do I decide if being a donor is right for me?</Text>
              </Text>
              <Text style={styles.answer}>
                Read as much as you can about the organ donation process. Discuss the pros and cons with your family, your doctor and your faith leader, if you have one. Ultimately the decision is up to you but some people find it helpful to know how their family members would feel about their decision.
              </Text>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.nextButton}
                onPress={handleNext}
              >
                <Text style={styles.nextButtonText}>Next</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  scrollContainer: {
    flex: 1,
  },
  content: {
    padding: ms(20),
    paddingBottom: ms(40),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: '600',
    color: '#2C4364',
    marginBottom: ms(30),
    textAlign: 'center',
  },
  faqContainer: {
    marginBottom: ms(30),
  },
  question: {
    fontSize: ms(18, 0.3),
    color: '#2C4364',
    marginBottom: ms(10),
    lineHeight: ms(24, 0.3),
  },
  bold: {
    fontWeight: 'bold',
  },
  answer: {
    fontSize: ms(16, 0.3),
    color: '#666666',
    marginBottom: ms(25),
    lineHeight: ms(22, 0.3),
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  nextButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  nextButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
}); 